import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { tap, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { UserService } from '../../services/users/user.service';
import { UserActions } from './user.actions';
import { IUser, IUsers } from '../../interfaces/user.interface';
import { Auth } from '../auth/auth.actions';
import { Pagination } from '../../interfaces/deliveries.interface';

export interface UserStateModel {
  user: IUser | null;
  editingUser: IUser | null;
  loading: boolean;
  meLoading: boolean;
  editingUserLoading: boolean;
  error: any | null;
  users: Pagination<IUsers>;
  usersSearchQuery: string;
  createWarning: string | null;
}

@State<UserStateModel>({
  name: 'user',
  defaults: {
    user: null,
    editingUser: null,
    loading: false,
    meLoading: false,
    editingUserLoading: false,
    error: null,
    users: { items: [], page: 1, pageSize: 10, total: 0 },
    usersSearchQuery: '',
    createWarning: null,
  },
})

@Injectable()
export class UserState {
  constructor(private userService: UserService) {}

  @Selector()
  static getUser(state: UserStateModel): IUser | null {
    return state.user;
  }

  @Selector()
  static getEditingUser(state: UserStateModel): IUser | null {
    return state.editingUser;
  }

  @Selector()
  static getIsSuperAdmin(state: UserStateModel): boolean {
    return state.user?.isSuperAdmin ?? false;
  }

  @Selector()
  static isLoading(state: UserStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static isMeLoading(state: UserStateModel): boolean {
    return state.meLoading;
  }

  @Selector()
  static isEditingUserLoading(state: UserStateModel): boolean {
    return state.editingUserLoading;
  }

  @Selector()
  static getError(state: UserStateModel): any | null {
    return state.error;
  }

  @Selector()
  static getCreateWarning(state: UserStateModel): string | null {
    return state.createWarning;
  }

  @Selector()
  static getUsers(state: UserStateModel): Pagination<IUsers> {
    return state.users;
  }

  @Action(UserActions.GetMe)
  getMe(ctx: StateContext<UserStateModel>) {
    const hasUser = !!ctx.getState().user;
    ctx.patchState({ meLoading: !hasUser, error: null });
    return this.userService.getMe().pipe(
      tap({
        next: (user) => {
          ctx.patchState({ user: user.data, meLoading: false });
          ctx.dispatch(new Auth.SetUser(user.data));
        },
        error: (error) => {
          ctx.patchState({ meLoading: false, error: error.error?.message });
        },
      }),
      catchError((error) => {
        ctx.patchState({ meLoading: false, error: error.error?.message });
        return throwError(() => error);
      })
    );
  }

  @Action(UserActions.SetMe)
  setMe(ctx: StateContext<UserStateModel>, action: UserActions.SetMe) {
    ctx.patchState({ user: action.user });
  }

  @Action(UserActions.UpdateMe)
  updateMe(ctx: StateContext<UserStateModel>, action: UserActions.UpdateMe) {
    ctx.patchState({ loading: true, error: null });
    return this.userService.patchProfile(action.data).pipe(
      tap({
        next: () => {
          ctx.dispatch(new UserActions.GetMe());
          ctx.patchState({ loading: false });
        },
        error: (error) => {
          ctx.patchState({ loading: false, error: error.error?.message });
        },
      }),
      catchError((error) => {
        ctx.patchState({ loading: false, error: error.error?.message });
        return throwError(() => error);
      })
    );
  }

  @Action(UserActions.UploadFile)
  uploadFile(
    ctx: StateContext<UserStateModel>,
    action: UserActions.UploadFile
  ) {
    ctx.patchState({ loading: true, error: null });
    return this.userService.uploadFile(action.userId, action.file).pipe(
      tap({
        next: () => {
          ctx.dispatch(new UserActions.GetMe());
          ctx.patchState({ loading: false });
        },
        error: (error) => {
          ctx.patchState({ loading: false, error: error.error?.message });
        },
      }),
      catchError((error) => {
        ctx.patchState({ loading: false, error: error.error?.message });
        return throwError(() => error);
      })
    );
  }

  @Action(UserActions.GetUsers)
  getUsers(ctx: StateContext<UserStateModel>, action: UserActions.GetUsers) {
    ctx.patchState({ loading: true, error: null });
    return this.userService.getUsers(action.page, action.pageSize, action.q).pipe(
      tap({
        next: (response) => {
          ctx.patchState({ users: response.data, usersSearchQuery: action.q ?? '', loading: false });
        },
        error: (error) => {
          ctx.patchState({ loading: false, error: error.error?.message });
        },
      })
    );
  }

  @Action(UserActions.GetUserById)
  getUserById(ctx: StateContext<UserStateModel>, action: UserActions.GetUserById) {
    ctx.patchState({ editingUserLoading: true });
    return this.userService.getUserById(action.id).pipe(
      tap({
        next: (response) => {
          ctx.patchState({
            editingUser: response.data,
            editingUserLoading: false,
          });
        },
        error: () => {
          ctx.patchState({
            editingUserLoading: false,
          });
        },
      })
    );
  }

  @Action(UserActions.ClearEditingUser)
  clearEditingUser(ctx: StateContext<UserStateModel>) {
    ctx.patchState({ editingUser: null, editingUserLoading: false });
  }


  @Action(UserActions.DeleteUser)
  deleteUser(ctx: StateContext<UserStateModel>, action: UserActions.DeleteUser){
    ctx.patchState({ loading: true, error: null });
    return this.userService.deleteUser(action.id).pipe(
      tap({
        next: () => {
          const { page, pageSize } = ctx.getState().users;
          const q = ctx.getState().usersSearchQuery;
          ctx.dispatch(new UserActions.GetUsers(page, pageSize, q || undefined));
          ctx.patchState({ loading: false });
        },
        error: (error) => {
          ctx.patchState({ loading: false, error: error.error?.message });
        },
      }),
      catchError((error) => {
        ctx.patchState({ loading: false, error: error.error?.message });
        return throwError(() => error);
      })
    );
  }

  @Action(UserActions.CreateUser)
  createUser(ctx: StateContext<UserStateModel>, action: UserActions.CreateUser){
    ctx.patchState({ loading: true, error: null, createWarning: null });
    return this.userService.createUser(action.data).pipe(
      tap({
        next: (response) => {
          const { page, pageSize } = ctx.getState().users;
          const q = ctx.getState().usersSearchQuery;
          ctx.dispatch(new UserActions.GetUsers(page, pageSize, q || undefined));
          ctx.patchState({
            loading: false,
            createWarning: response.emailWarning ?? null,
          });
        },
        error: (error) => {
          ctx.patchState({ loading: false, error: error.error?.message });
        },
      }),
      catchError((error) => {
        ctx.patchState({ loading: false, error: error.error?.message });
        return throwError(() => error);
      })
    );
  }

  @Action(UserActions.UpdateUser)
  updateUser(ctx: StateContext<UserStateModel>, action: UserActions.UpdateUser){
    ctx.patchState({ loading: true, error: null });
    return this.userService.updateUser(action.id, action.data).pipe(
      tap({
        next: () => {
          const { page, pageSize } = ctx.getState().users;
          const q = ctx.getState().usersSearchQuery;
          ctx.dispatch([
            new UserActions.GetUsers(page, pageSize, q || undefined),
            new UserActions.GetMe(),
          ]);
          ctx.patchState({ loading: false });
        },
        error: (error) => {
          ctx.patchState({ loading: false, error: error.error?.message });
        },
      }),
      catchError((error) => {
        ctx.patchState({ loading: false, error: error.error?.message });
        return throwError(() => error);
      })
    );
  }

  @Action(UserActions.GetDeletedUsers)
  getDeletedUsers(ctx: StateContext<UserStateModel>) {
    ctx.patchState({ loading: true, error: null });
      return this.userService.getDeletedUsers().pipe(
        tap({
          next: (response) => {
            const items = response.data.map(u => ({ ...u, isDeleted: true }));
            ctx.patchState({
              users: { items, page: 1, pageSize: items.length || 10, total: items.length },
              loading: false,
            });
          },
          error: (error) => {
            ctx.patchState({ loading: false, error: error.error?.message });
          },
        })
      );
  }

  @Action(UserActions.RestoreDeletedUser)
  restoreDeletedUser(ctx: StateContext<UserStateModel>, action: UserActions.RestoreDeletedUser){
    ctx.patchState({ loading: true, error: null });
    return this.userService.restoreDeletedUser(action.id).pipe(
      tap({
        next: () => {
          ctx.dispatch(new UserActions.GetDeletedUsers());
          ctx.patchState({ loading: false });
        },
        error: (error) => {
          ctx.patchState({ loading: false, error: error.error?.message });
        },
      }),
      catchError((error) => {
        ctx.patchState({ loading: false, error: error.error?.message });
        return throwError(() => error);
      })
    );
  }


}
